// redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { mapUserRoles } from '../../functions/useRoleMapping';
import { getCustomRoles } from '../../functions/useCustomRoles';

// Thunk to fetch user roles and details
export const fetchUserRoles = createAsyncThunk('user/fetchUserRoles', async ({ instance, accounts }, { rejectWithValue, getState }) => {
    if (accounts.length === 0) return rejectWithValue("No accounts found");

    const account = accounts[0];

    // Define group IDs
    const groupIds = {
        Admin: process.env.REACT_APP_GROUP_ADMIN,
        Apps: process.env.REACT_APP_GROUP_APPS,
        AppsManager: process.env.REACT_APP_GROUP_APPSMANAGER,
        ControlEngineer: process.env.REACT_APP_GROUP_CONTROL,
        ElectricalEngineer: process.env.REACT_APP_GROUP_ELEC,
        MechanicalEngineer: process.env.REACT_APP_GROUP_MECH,
        Engineer: process.env.REACT_APP_GROUP_ENGINEER,
        TestPit: process.env.REACT_APP_GROUP_TESTPIT,
        PSN: process.env.REACT_APP_GROUP_PSN, 
        Sales: process.env.REACT_APP_GROUP_SALES,
        ReadOnly: process.env.REACT_APP_GROUP_READONLY,
        Scheduling: process.env.REACT_APP_GROUP_SCHEDULING,
        EngineerManager: process.env.REACT_APP_GROUP_ENGINEERADMIN,
        Accounting: process.env.REACT_APP_GROUP_ACCOUNTING,
        NorthPrairie: process.env.REACT_APP_GROUP_NP,
        AssemblySchedule: process.env.REACT_APP_GROUP_ASSEMBLYSCHEDULE,
    };

    console.log(groupIds)

    try {
        const request = {
            scopes: ["https://graph.microsoft.com/.default"],
            account: account,
        };
        const response = await instance.acquireTokenSilent(request);
        const accessToken = response.accessToken;

        const userGroups = await fetch(`https://graph.microsoft.com/v1.0/me/memberOf`, {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => res.json());

        const matchingRoles = [];

        for (const role in groupIds) {
            if (userGroups.value.some((group) => group.id === groupIds[role])) {
                matchingRoles.push(role);
            }
        }

        // Ensure any specific engineer role also gets the 'Engineer' role
        if (matchingRoles.includes('ControlEngineer') || matchingRoles.includes('ElectricalEngineer') || matchingRoles.includes('MechanicalEngineer')) {
            if (!matchingRoles.includes('Engineer')) {
                matchingRoles.push('Engineer');
            }
        }

        if (matchingRoles.length === 0) {
            matchingRoles.push("ReadOnly");
        }

        // Add custom roles based on user email
        const state = getState();
        const customRole = getCustomRoles(account.username, state);
        if (customRole && !matchingRoles.includes(customRole)) {
            matchingRoles.push(customRole);
        }

        // Convert non-serializable Map objects or omit them
        const serializedUserDetails = {
            ...account,
            tenantProfiles: Array.from(account.tenantProfiles || []) // Convert Map to array
        };

        // Pre-map roles with the complete state
        const rolesMapped = mapUserRoles(matchingRoles, account.username, state);

        return { 
            userRoles: matchingRoles, 
            userDetails: serializedUserDetails,
            rolesMapped: rolesMapped 
        };
    } catch (error) {
        console.error("Error fetching user groups:", error);
        return rejectWithValue("Error fetching user roles");
    }
});

export const userSlice = createSlice({
    name: 'user',
    initialState: {
        userDetails: null,
        userRoles: [],
        rolesMapped: {},
        status: 'idle',
        error: null
    },
    reducers: {
        clearUserRoles: (state) => {
            state.userRoles = [];
            state.rolesMapped = {};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserRoles.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchUserRoles.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.userDetails = action.payload.userDetails;
                state.userRoles = action.payload.userRoles;
                
                // Use the pre-mapped roles from the thunk
                state.rolesMapped = action.payload.rolesMapped;
            })
            .addCase(fetchUserRoles.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

// Selector to access user roles and details
export const selectUserRoles = (state) => state.user.userRoles;
export const selectUserDetails = (state) => state.user.userDetails;
export const selectMappedRoles = (state) => state.user.rolesMapped;

//Export the clearUserRoles action
export const { clearUserRoles } = userSlice.actions;

export default userSlice.reducer;
